import { track } from '@sloyd/analytics';
import * as api from '@sloyd/api';
import { actions, selectors } from '@sloyd/store-base';
import { prompt, toast } from '@sloyd/ui';
import { bridge } from '@sloyd/viewer';
import { call, delay, fork, put, select, takeEvery } from 'saga-ts';
import { $a, downloadJson, guid4, invokeEvent, l, setNumber } from 'shared-base';
import ModalMobile from '../components/ModalMobile/ModalMobile';
import { exportGuard } from './helpers/guards';
import { getIdAndName } from './helpers/idAndName';

export const saga = {
  id: 'sloyd.editor',
  type: 'entity',
  root: root,
  trigger: {
    actionTypes: ['EDITOR'],
  },
};

type Verb =
  | 'toggleMode'
  | 'recipe'
  | 'copyGeneratorId'
  | 'nextModel'
  | 'prevModel'
  | 'resizeNavBar'
  | 'qrCode'
  | 'preview'
  | 'copy'
  | 'paste'
  | 'download';

type Action = {
  type: 'EDITOR';
  verb: Verb;
  id: string;
  payload: Json;
  origin?: string;
};

const map: Record<Verb, any> = {
  toggleMode: toggleMode,
  download: download,
  preview: preview,
  recipe: recipe,
  copyGeneratorId: copyGeneratorId,
  nextModel: nextModel,
  prevModel: prevModel,
  copy: copy,
  qrCode: qrCode,
  paste: paste,
  resizeNavBar: resizeNavBar,
};

const analyitcsMapEditor: any = {
  toggleMode: 'Toggle Mode',
  download: 'Download Model Start',
  preview: 'Preview',
  recipe: 'Recipe',
  copyGeneratorId: 'Copy Generator ID',
  nextModel: 'Next Model',
  prevModel: 'Previous Model',
  copy: 'Copy',
  paste: 'Paste',
  qrCode: 'QRCode',
  resizeNavBar: 'Resize Navbar',
};

export function* qrCode(action: Action) {
  const url = document.location.href.replace('editor', 'mobile');

  try {
    yield prompt.custom({
      title: 'View in Mobile',
      component: ModalMobile,
      componentProps: {
        url,
      },
    });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* recipe(action: Action) {
  try {
    const model = yield* select(selectors.single.$currentModel);

    if (!model) return;

    const { generatorId } = model;

    bridge.patchEnvironment('main', { areGridLinesVisible: false });
    yield delay(10);
    downloadJson(generatorId + '.json', model);
    bridge.downloadScreenshot('main', generatorId);
    yield delay(10);
    bridge.patchEnvironment('main', { areGridLinesVisible: true });
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* resizeNavBar(action: Action) {
  try {
    const { payload } = action;
    const { width, isLeft } = payload;

    const MIN_WIDTH = 210;
    const MAX_WIDTH = 700;

    const adjustedWidth = Math.min(Math.max(width, MIN_WIDTH), MAX_WIDTH);

    const key = isLeft ? 'navbarWidthLeft' : 'navbarWidthRight';
    yield put(actions.appState.patch({ [key]: adjustedWidth }));

    if (!isLeft) {
      invokeEvent('MaterialSelector/resize');
    }

    setNumber(key, adjustedWidth);
  } catch (err) {
    invokeEvent('send', { err });
  }
}

export function* copy(action: Action) {
  // clipboard
  try {
    const { id } = action;
    let model: IModel;

    if (id) {
      model = yield* select(selectors.single.$model, id);
    } else {
      model = yield* select(selectors.single.$currentModel);
    }

    navigator.clipboard.writeText(JSON.stringify(model, null, 2));

    toast.show('Model copied to clipboard');
  } catch (err) {
    invokeEvent('send', { err });
  }
}

export function* paste(action: Action) {
  const { payload } = action;
  const { json } = payload;

  const modelId = guid4();
  delete json.id;

  yield put(
    $a('MODELS', 'create', modelId, {
      ...json,
    })
  );

  toast.show('Model pasted as a new model');
}

export function* copyGeneratorId(action: Action) {
  const model = yield* select(selectors.raw.$rawModel);
  if (!model) return;

  const modelJson = JSON.stringify(model, null, 4);

  navigator.clipboard.writeText(modelJson);

  toast.show('Model copied to clipboard');
}

export function* nextModel(action: Action) {
  const nextPrev = yield* select(selectors.base.$nextPrevModels);
  if (!nextPrev.nextExists) return;

  invokeEvent('nav', { path: nextPrev.nextPath });
}

export function* prevModel(action: Action) {
  const nextPrev = yield* select(selectors.base.$nextPrevModels);
  if (!nextPrev.prevExists) return;

  invokeEvent('nav', { path: nextPrev.prevPath });
}

export function* preview(action: Action) {
  yield delay(10);

  bridge.getGlb('main');
}

export function* download(action: Action) {
  let generatorId = '';

  try {
    const { payload } = action;
    const { fileFormat } = payload;

    const canPass = yield* exportGuard();

    const model = yield* select(selectors.single.$currentModel);
    if (!model) {
      throw new Error('Model not found');
    }

    generatorId = model.generatorId;

    if (!canPass) {
      track('Download Model Not Enough Credits', { fileFormat, generatorId });
      throw new Error('Not enough credits to export');
    }

    if (fileFormat === 'JSON') {
      downloadJson(generatorId + '.json', model);
      track('Download Model Success', { fileFormat, generatorId });
      return;
    }

    const downloadInfo = {
      ProjectName: generatorId,
      Format: fileFormat.toLowerCase(),
      IsSolid: false,
    };

    const resDeduct = yield api.user.deductCreditsForExport();

    if (!resDeduct || !resDeduct.isSuccess) {
      toast.error('Could not deduct credits');
      throw new Error('Could not deduct credits');
    }

    bridge.download('main', downloadInfo);
    track('Download Model Success', { fileFormat, generatorId });
    yield delay(1000);
    invokeEvent('REFRESH_USER');
  } catch (err) {
    track('Download Model Error', { err, generatorId });
    yield put(actions.appState.patch({ isCharging: false }));
    invokeEvent('sentry', { err });
  }
}

export function* toggleMode(action: Action) {
  try {
    const { payload = {} } = action;
    const { mode } = payload;

    const editMode = yield* select(selectors.base.$editMode);

    let newMode = editMode === 'object' ? 'material' : 'object';

    if (mode) {
      newMode = mode;
    }

    yield put(actions.appState.patch({ editMode: newMode as any }));
  } catch (err) {
    invokeEvent('sentry', { err });
  }
}

export function* editor(action: any) {
  let { verb, id, origin } = action;
  yield delay(10);

  const saga = (map as any)[verb];

  if (!saga) {
    return;
  }

  const idAndName = yield* call(getIdAndName);
  track(analyitcsMapEditor[verb], { origin, ...idAndName });

  l(`saga|saga.editor ${verb} on ${id}`);

  yield* fork(saga, action);
}

export function* root() {
  yield takeEvery('EDITOR', editor);
}
